import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as queryString from 'query-string';
import PropTypes from 'prop-types';

import withAuth from '../../../components/hocs/withAuth';
import withStatsig from '../../../components/hocs/withStatsig';
import SignInForm from '../../../components/SignIn';
import { screens } from '../../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { PopupTypes } from '../../../utils/constants';

import { signInEmail } from '../../../actions';
import * as popupActions from '../../../actions/popup-actions';
import { connectThirdPartyId } from '../../../actions/activate-utr-actions';
import { getUserAge, isUtrActivationInProgress, shouldCallConnectPlayerThirdPartyId, shouldNavigateToRedirectUrl, UtrActivationUnderAgeLimit } from '../../../utils/activateUtr';
import { applyPostLoginRedirect, resolvePostLoginRedirect, resolveThirdPartyRedirect } from '../../../utils/loginRedirect';
import { withCurrentReturnTo } from '../../../utils/oauthReturnTo';
import { resolveMagicLinkCallbackUrl, resolveMagicLinkContext } from '../../../utils/magicLinkCallback';

import config from '../../../../config';

import styles from './login.scss';
import TennisAustraliaLogin from '../../../components/TennisAustraliaLogin';
import AppleLoginV3 from '../../../components/AppleLoginV3';
import FacebookLoginV2 from '../../../components/FacebookLoginV2';
import MagicLinkLoginFlow from '../MagicLinkLoginFlow';

class Login extends Component {
    constructor(props) {
        super(props);
        this.submit = this.submit.bind(this);
        this.joinNow = this.joinNow.bind(this);
        if (props?.auth?.user) {
            if (props?.inPopUp) {
                props.moveToNextScreen();
            } else {
                const parsed = queryString.parse(this.props.location.search);
                // tpr (third-party redirect) routes the user to a partner destination after
                // auth — same-tab via window.location.href because we're outside a click
                // gesture and `window.open(..., '_blank')` would be popup-blocked.
                // Sits just above `redirect`; `next` wins.
                const tprUrl = resolveThirdPartyRedirect(parsed.tpr, props.auth.user);
                if (parsed.next) {
                    props.history.replace(parsed.next);
                } else if (tprUrl) {
                    window.location.href = tprUrl;
                } else if (parsed.redirect) {
                    window.open(parsed.redirect, '_self');
                } else {
                    props.history.replace('/home'); // Go to dashboard if logged in
                }
            }
        }
        this.state = {
            submitting: false,
        };
    }

    async componentDidUpdate() {
        if (this.props.auth.user) {
            const { mfaOptions } = this.props.auth.user;
            if (mfaOptions?.mfaRequired) {
                const search = this.props.location?.search || '';
                if (mfaOptions?.mfaEnabled) {
                    this.props.history.push({ pathname: '/mfa', search });
                } else {
                    this.props.history.push({ pathname: '/mfa-setup', search });
                }
            } else if (!this.props.inPopUp) {
                const parsed = queryString.parse(this.props.location.search);
                const {
                    onboardingRequired,
                    onboardingCompleted,
                } = this.props.auth.user;

                const utrActivationInProgress = isUtrActivationInProgress(this.props.activateUtr);

                if (onboardingRequired && !onboardingCompleted) {
                    let queryParams = {};
                    if (utrActivationInProgress) {
                        queryParams = {
                            ...parsed,
                            activationType: this.props.activateUtr.activationType,
                            activationToken: this.props.activateUtr.activationToken,
                        };
                    } else {
                        queryParams = { ...parsed };
                        if (parsed.next && !parsed.return) {
                            queryParams.return = parsed.next;
                            delete queryParams.next;
                        }
                    }
                    const url = `/onboarding${Object.keys(queryParams).length > 0 ? `?${queryString.stringify(queryParams, { encode: true })}` : ''}`;
                    this.props.history.replace(url);

                    return;
                }

                if (utrActivationInProgress) {
                    if (shouldCallConnectPlayerThirdPartyId(this.props.activateUtr)) {
                        await this.props.connectPlayerThirdPartyId({
                            type: this.props.activateUtr.activationType,
                            token: this.props.activateUtr.activationToken,
                        });
                    }

                    if (!shouldNavigateToRedirectUrl(this.props.activateUtr)) {
                        return;
                    }
                }

                applyPostLoginRedirect(
                    resolvePostLoginRedirect(parsed, this.props.activateUtr, this.props.auth.user),
                    this.props.history,
                );
            } else {
                this.props.moveToNextScreen();
            }
        }
    }

    joinNow() {
        const { location, inPopUp, history, moveToScreen, showPopup, activateUtr } = this.props;
        if (inPopUp) {
            moveToScreen(screens.SIGN_UP);
        } else {
            const parsed = queryString.parse(location.search);
            const { next } = parsed;
            const joinParsed = parsed;
            if (next) {
                joinParsed.return = encodeURIComponent(next);
                delete joinParsed.next;
            }
            const joinUrl = `/join${Object.keys(joinParsed).length > 0 ? `?${queryString.stringify(joinParsed, { encode: false })}` : ''}`;

            if (isUtrActivationInProgress(activateUtr) && getUserAge(activateUtr) < UtrActivationUnderAgeLimit) {
                showPopup({
                    popup: PopupTypes.ACTIVATE_UTR_UNDER_AGE_POPUP,
                    onClosePopup: () => {
                        history.push(joinUrl);
                    },
                });
            } else {
                history.push(joinUrl);
            }
        }
    }

    async submit(values) {
        this.setState({
            submitting: true,
        });
        await this.props.signInEmail(values);
        this.setState({
            submitting: false,
        });
    }

    render() {
        const { auth, inPopUp, forceMobileUi, email, activateUtr, checkGate, featureGates } = this.props;
        const magicLinkLoginEnabled = checkGate(featureGates.MAGIC_LINK_LOGIN);
        const campaignDeepLinkEnabled = checkGate(featureGates.POWER_CAMPAIGN_DEEP_LINK);
        const parsed = queryString.parse(this.props.location.search);
        // Suppress the login form while componentDidUpdate is preparing a partner
        // redirect — window.location.href is a hard nav and React will otherwise
        // render once more before the browser leaves the page, flashing the form.
        // Higher-priority paths (MFA, Onboarding, next, upgradeAndRedirect) all
        // SPA-swap and don't flash, so leave the form visible when any of them
        // will fire instead of tpr.
        const mfaWillIntercept = !!auth.user?.mfaOptions?.mfaRequired;
        const onboardingWillIntercept = !!auth.user?.onboardingRequired && !auth.user?.onboardingCompleted;
        if (
            auth.user &&
            !inPopUp &&
            !mfaWillIntercept &&
            !onboardingWillIntercept &&
            !parsed.next &&
            !parsed.upgradeAndRedirect &&
            resolveThirdPartyRedirect(parsed.tpr, auth.user)
        ) {
            return null;
        }
        // ENG-2590: applied here too because MagicLinkLoginFlow bypasses AppleLoginV3/FacebookLoginV2.
        const withCampaignReturnTo = (url) => (campaignDeepLinkEnabled ? withCurrentReturnTo(url) : url);
        const appleAuthURL = withCampaignReturnTo(`${config.apiHost}/v3/oauth/apple/initiate?platform=web&flow=login`);
        const facebookOAuthV2URL = withCampaignReturnTo(`${config.apiHost}/v3/oauth/facebook/initiate?platform=web&flow=login`);
        const magicLinkCallbackUrl = campaignDeepLinkEnabled
            ? resolveMagicLinkCallbackUrl({ location: this.props.location, inPopUp })
            : null;
        // ENG-2611: not gated with the callback URL above. The callback URL restores a
        // destination; the context keeps the member in the browser their referral cookie
        // lives in, which matters even when no destination was stored. Gating both on the
        // campaign flag would mean turning that flag off silently re-breaks referral
        // redemption on iOS.
        const magicLinkContext = resolveMagicLinkContext({ location: this.props.location, inPopUp });

        const facebookAuthURL = facebookOAuthV2URL;

        let parsedTitle = '';

        switch (parsed.flow) {
            case 'search':
                parsedTitle = 'Log in to view search results';
                break;
            case 'club_create':
                parsedTitle = 'Log in to create your UTR Event';
                break;
            case 'high_school_club_create':
                parsedTitle = 'Log in to complete your UTR High School Club';
                break;
            default:
                parsedTitle = 'Sign In';
                break;
        }
        const title = this.props.title || parsedTitle;

        const utrActivationInProgress = isUtrActivationInProgress(activateUtr);
        const defaultEmail = email || (utrActivationInProgress ? activateUtr?.user?.email : '');

        return (
            <div className={`${styles.section} ${forceMobileUi ? styles.popUp : ''}`}>
                <div className={`container`}>
                    <div className="row">
                        <div className={`${forceMobileUi ? 'col' : 'col-12 col-md-6 offset-md-3'}`}>
                            {magicLinkLoginEnabled ? (
                                <MagicLinkLoginFlow
                                    isSubmitting={this.state.submitting}
                                    onSignInWithCredentials={this.submit}
                                    onJoinNowClick={this.joinNow}
                                    facebookUrl={facebookAuthURL}
                                    appleUrl={appleAuthURL}
                                    callbackUrl={magicLinkCallbackUrl}
                                    context={magicLinkContext}
                                />
                            ) : (
                                <div className={`${styles.authCard}`}>
                                    {parsed.flow === 'club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/6</div>
                                        </div>
                                    )}
                                    {parsed.flow === 'high_school_club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/5</div>
                                        </div>
                                    )}
                                    {parsed.flow === 'tournament_club_create' && (
                                        <div className="step-header">
                                            <div className="text-highlight2" data-testid="login.page.step-indicator">STEP 5/5</div>
                                        </div>
                                    )}
                                    <h3 className={`${styles.title}`} data-testid="login.page.title-heading">
                                        <span data-testid="login.page.title">{title}</span>
                                    </h3>
                                    {auth.error && (
                                        <p className={styles.errorText} data-testid="login.page.error-text">
                                            {auth.error}
                                        </p>
                                    )}
                                    <SignInForm onSubmit={this.submit} email={defaultEmail} submitting={this.state.submitting} isSignInFlow={parsedTitle === 'Sign In'} data-testid="login.page.sign-in-form" />
                                    <div className={`${styles.tos} mb-1`}>
                                        <span data-testid="login.page.tos">By continuing, I agree to the <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="login.page.tos-link">Terms of service</a>.</span>
                                    </div>
                                    <a className={`${styles.forgotLink}`} href={`/forgotpassword`} data-testid="login.page.forgot-password-link">Forgot Password?</a>
                                    <hr className="hr-text" data-content="or" />
                                    {!utrActivationInProgress && (
                                        <FacebookLoginV2 flow="login" buttonText="Sign In with Facebook" data-testid="login.page.facebook-btn" />
                                    )}
                                    <AppleLoginV3 flow="login" buttonText="Sign In with Apple" data-testid="login.page.apple-btn" />
                                    <TennisAustraliaLogin data-testid="login.page.tennis-australia-btn" />
                                    <div className={`${styles.divider}`} />
                                    <div className={`${styles.text}`} data-testid="login.page.no-account-text">
                                        <strong>Don&apos;t have an account?</strong> <span><a className="cursor-pointer text-teal" onClick={this.joinNow} data-testid="login.page.join-now-link">Join now.</a></span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        signInEmail: (data) => dispatch(signInEmail(data)),
        connectPlayerThirdPartyId: (data) => dispatch(connectThirdPartyId(data)),
        showPopup: (popUpData) => {
            dispatch(popupActions.showPopupNew(popUpData));
        },
    };
}

function mapStateToProps({ activateUtr }) {
    return { activateUtr };
}

Login.propTypes = {
    auth: PropTypes.objectOf(PropTypes.any).isRequired,
    checkGate: PropTypes.func.isRequired,
    featureGates: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStatsig(withAuth(connect(mapStateToProps, mapDispatchToProps)(Login)));
